/**
 * Centralized error messages for onboarding module
 * Following existing i18n patterns and Norwegian language usage
 */

// Error type constants
export const ERROR_TYPES = {
  NETWORK: 'network',
  VALIDATION: 'validation',
  AUTH: 'auth',
  CONFLICT: 'conflict',
  SERVER: 'server',
  UNKNOWN: 'unknown'
};

// Centralized error messages (Norwegian - following existing codebase pattern)
export const ERROR_MESSAGES = {
  // Network errors
  NETWORK_ERROR: 'Nettverksfeil. Kontroller internettforbindelsen og prøv igjen.',
  
  // Server errors
  SERVER_ERROR: 'Serverfeil. Prøv igjen senere eller kontakt kundeservice.',
  
  // Authentication errors
  AUTH_ERROR: 'Autorisering feilet. Logg inn på nytt.',
  ACCESS_DENIED: 'Ingen tilgang til denne funksjonen.',
  
  // Validation errors
  VALIDATION_GENERAL: 'Valideringsfeil. Kontroller at alle felt er riktig utfylt.',
  VALIDATION_REGISTRATION: 'Det oppstod en valideringsfeil. Vennligst kontakt kundeservice for hjelp.',
  VALIDATION_EMAIL: 'E-postfeil. Kontroller e-postadressen og prøv igjen.',
  VALIDATION_COMPANY: 'Bedriftsopplysninger mangler eller er feil. Kontroller og prøv igjen.',
  VALIDATION_COMPANY_ADDRESS: 'Organisasjonen mangler adresseopplysninger. Gå tilbake til steg 1 og fyll inn adresse.',
  VALIDATION_COMPANY_INFO_INCOMPLETE: 'Bedriftsinformasjon er ufullstendig. Kontroller organisasjonsnummer og adresse.',
  
  // Conflict errors (409)
  USER_EXISTS: 'Bruker eksisterer allerede. Prøv å logge inn i stedet.',
  ORG_NUMBER_EXISTS: 'Sjekk at opplysninger stemmer.',
  ORG_NUMBER_IN_REGISTRATION: 'Vi kan ikke fullføre registreringen med dette organisasjonsnummeret. Vennligst kontakt Kreditorforeningen for assistanse.',
  CONFLICT_GENERAL: 'Datakonflikt. Opplysningene finnes allerede i systemet.',
  
  // Generic errors
  GENERIC_ERROR: 'Noe gikk galt. Prøv igjen senere.',
  
  // Context-specific errors
  EMAIL_VERIFICATION_FAILED: 'Kunne ikke bekrefte e-postadresse. Prøv igjen senere.',
  EMAIL_RESEND_FAILED: 'Kunne ikke sende e-post på nytt. Prøv igjen senere.',
  COMPANY_SEARCH_FAILED: 'Kunne ikke hente bedriftsopplysninger. Prøv igjen senere.',
  REGISTRATION_FAILED: 'Registrering feilet. Kontroller opplysningene og prøv igjen.',
  SETUP_FAILED: 'Oppsett feilet. Kontakt kundeservice for hjelp.',
  
  // Field validation messages (for forms)
  FIELD_REQUIRED: 'Dette feltet er påkrevd.',
  FIELD_EMAIL_INVALID: 'Ugyldig e-postadresse.',
  FIELD_PHONE_INVALID: 'Ugyldig telefonnummer.',
  FIELD_ORG_NUMBER_INVALID: 'Ugyldig organisasjonsnummer.',
  FIELD_NAME_INVALID: 'Ugyldig navn.'
};

// Context mappings for automatic error message selection
export const ERROR_CONTEXTS = {
  REGISTRATION: 'registration',
  EMAIL_VERIFICATION: 'email_verification',
  EMAIL_RESEND: 'email_resend',
  COMPANY_INFO: 'company_info',
  COMPANY_SEARCH: 'company_search',
  SETUP: 'setup',
  LOGIN: 'login',
  GENERAL: 'general'
};

// Mapping of context to specific error messages
export const CONTEXT_ERROR_MESSAGES = {
  [ERROR_CONTEXTS.REGISTRATION]: {
    default: ERROR_MESSAGES.REGISTRATION_FAILED,
    validation: ERROR_MESSAGES.VALIDATION_REGISTRATION
  },
  [ERROR_CONTEXTS.EMAIL_VERIFICATION]: {
    default: ERROR_MESSAGES.EMAIL_VERIFICATION_FAILED,
    validation: ERROR_MESSAGES.VALIDATION_EMAIL
  },
  [ERROR_CONTEXTS.EMAIL_RESEND]: {
    default: ERROR_MESSAGES.EMAIL_RESEND_FAILED
  },
  [ERROR_CONTEXTS.COMPANY_INFO]: {
    default: ERROR_MESSAGES.VALIDATION_COMPANY,
    validation: ERROR_MESSAGES.VALIDATION_COMPANY
  },
  [ERROR_CONTEXTS.COMPANY_SEARCH]: {
    default: ERROR_MESSAGES.COMPANY_SEARCH_FAILED
  },
  [ERROR_CONTEXTS.SETUP]: {
    default: ERROR_MESSAGES.SETUP_FAILED
  },
  [ERROR_CONTEXTS.GENERAL]: {
    default: ERROR_MESSAGES.GENERIC_ERROR
  }
};

/**
 * Get context-specific error message
 * @param {string} context - Error context
 * @param {string} errorType - Type of error
 * @returns {string} Appropriate error message
 */
export const getContextErrorMessage = (context, errorType = 'default') => {
  const contextMessages = CONTEXT_ERROR_MESSAGES[context];
  if (contextMessages && contextMessages[errorType]) {
    return contextMessages[errorType];
  }
  return ERROR_MESSAGES.GENERIC_ERROR;
};